import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Link as MuiLink,
} from "@mui/material";

const PrivacyPolicy = () => {
  const BulletList = ({ items }) => (
    <Box component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
      {items.map((item, idx) => (
        <Typography component="li" variant="body1" key={idx} sx={{ mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          backgroundColor: "#ffffff",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Main Heading */}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.75rem", sm: "2rem" },
            textAlign: "center",
            mb: 3,
          }}
        >
          Privacy Policy for VIP Plus Valet Service App
        </Typography>

        {/* 1. Introduction */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            VIP Plus Valet Service App (“Valet Parking Control”) is a valet
            parking management solution designed to facilitate vehicle check-in,
            tracking, and retrieval services. This Privacy Policy explains how
            we collect, use, disclose, and safeguard your information when you
            use the App.
          </Typography>
          <Typography variant="body1">
            By using the App, you agree to the collection and use of information
            in accordance with this Privacy Policy.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 2. Information We Collect */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            2. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            We may collect the following types of information:
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mt: 2 }}
          >
            2.1 Personal Information
          </Typography>
          <BulletList
            items={[
              "Customer Name (if provided)",
              "Phone number (if provided)",
              "Vehicle details (e.g., license plate number, car model)",
            ]}
          />

          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mt: 2 }}
          >
            2.2 Operational Data
          </Typography>
          <BulletList
            items={[
              "Check-in and check-out timestamps",
              "Parking location and vehicle status",
              "Ticket or transaction identifiers",
            ]}
          />

          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mt: 2 }}
          >
            2.3 Device Information
          </Typography>
          <BulletList
            items={[
              "Device type and operating system",
              "App usage data (for performance and analytics)",
            ]}
          />

          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mt: 2 }}
          >
            2.4 Communication Data
          </Typography>
          <BulletList
            items={[
              "Notifications sent within the App (e.g., valet requests, driver alerts)",
              "SMS or message-based links used for vehicle retrieval",
            ]}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 3. How We Use Your Information */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use the collected information to:
          </Typography>
          <BulletList
            items={[
              "Facilitate vehicle check-in and check-out processes",
              "Notify drivers for vehicle handling and retrieval",
              "Enable customers to request their vehicles via mobile links",
              "Generate digital or printed parking tickets",
              "Improve operational efficiency and service quality",
              "Monitor and analyze app performance",
              "Ensure security and prevent misuse",
            ]}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 4. How the App Works (Data Usage Context) */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            4. How the App Works (Data Usage Context)
          </Typography>
          <Typography variant="body1" paragraph>
            The App operates as follows:
          </Typography>
          <BulletList
            items={[
              "Upon arrival, customer and vehicle details (e.g., license plate) are registered",
              "A driver receives a notification to park the vehicle",
              "The vehicle status is tracked within the system",
              "Customers receive a link (via SMS or similar) to request their vehicle",
              "When requested, a driver is notified to retrieve and deliver the vehicle",
              "Optional printed tickets may be issued and used for retrieval",
            ]}
          />
          <Typography variant="body1">
            All related data is processed solely to ensure smooth valet
            operations.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 5. Sharing of Information */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            5. Sharing of Information
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell or rent your personal data.
          </Typography>
          <Typography variant="body1" paragraph>
            We may share information only in the following cases:
          </Typography>
          <BulletList
            items={[
              "With authorized valet staff and operators to perform services",
              "With service providers that support app functionality (e.g., hosting, messaging services)",
              "When required by law or legal obligations",
            ]}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 6. Data Retention */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            6. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your data only for as long as necessary to:
          </Typography>
          <BulletList
            items={[
              "Provide valet services",
              "Maintain operational records",
              "Comply with legal obligations",
            ]}
          />
          <Typography variant="body1">
            After that, data may be securely deleted or anonymized.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 7. Data Security */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            7. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational measures to
            protect your data, including:
          </Typography>
          <BulletList
            items={[
              "Secure data storage",
              "Access control for authorized personnel only",
              "Protection against unauthorized access or misuse",
            ]}
          />
          <Typography variant="body1">
            However, no system is 100% secure, and we cannot guarantee absolute
            security.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 8. Your Rights */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            8. Your Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have the right to:
          </Typography>
          <BulletList
            items={[
              "Access your personal data",
              "Request correction of inaccurate data",
              "Request deletion of your data",
              "Object to or restrict processing",
            ]}
          />
          <Typography variant="body1">
            To exercise your rights, please contact us using the details below.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 9. Third-Party Services */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            9. Third-Party Services
          </Typography>
          <Typography variant="body1">
            The App may use third-party services (e.g., SMS gateways, analytics
            tools). These providers may process limited data strictly for
            service functionality.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 10. Children’s Privacy */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            10. Children’s Privacy
          </Typography>
          <Typography variant="body1">
            The App is not intended for use by individuals under the age of 13
            (or applicable minimum age in your region). We do not knowingly
            collect data from children.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 11. Changes to This Policy */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            11. Changes to This Policy
          </Typography>
          <Typography variant="body1">
            We may update this Privacy Policy from time to time. Any changes
            will be posted within the App or on our website with an updated
            “Effective Date.”
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 12. Contact Us */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            12. Contact Us
          </Typography>
          <Typography variant="body1" gutterBottom>
            If you have any questions about this Privacy Policy, please contact
            us:
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1">
              <strong>Company Name:</strong> Haber Holding SAL
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong>{" "}
              <MuiLink
                href="mailto:info@vipparkingcontrol.com"
                underline="hover"
              >
                info@vipparkingcontrol.com
              </MuiLink>
            </Typography>
            <Typography variant="body1">
              <strong>Website:</strong>{" "}
              <MuiLink
                href="https://vipparkingcontrol.com/contact-us/"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                https://vipparkingcontrol.com/contact-us/
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
